import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PageRoutes } from "../../../constants/routes";
import { appActions } from "../../../ducks/app";
import {
  User,
  userActions,
  UserRole,
  userSelectors,
} from "../../../ducks/user";
import { useNavigation } from "../../../hooks/useNavigation";
import { TableHeaderItem, TableSortBy } from "../../../hooks/useTable";
import { AppDispatch } from "../../../store";
import { formatDateTime, isNowInDateRange } from "../../../utils/date";
import { Link } from "../../atoms/Link";
import { authSelectors } from "../../../ducks/auth";

export const useUsers = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { navigateToPage } = useNavigation();

  const _users = useSelector(userSelectors.users);
  const loggedInUser = useSelector(authSelectors.loggedInUser);

  const users = useMemo(() => {
    return _users?.map((user) => {
      return {
        ...user,
        invalid: !isNowInDateRange(user.validPeriod.from, user.validPeriod.to),
      };
    });
  }, [_users]);

  const canCreate = useMemo(() => {
    if (!loggedInUser) return false;

    return loggedInUser.role === UserRole.Admin;
  }, [loggedInUser]);

  const fetchUsers = useCallback((sort: TableSortBy) => {
    dispatch(appActions.showLoading());

    dispatch(
      userActions.fetchUsers.started({
        // @ts-expect-error
        sort_key: sort.field,
        sort_order: sort.order,
        onComplete: () => dispatch(appActions.hideLoading()),
      }),
    );
  }, []);

  const tableHeaders: TableHeaderItem<User>[] = useMemo(() => {
    return [
      {
        id: 1,
        title: "氏名",
        popupTitle: "名前",
        dataKey: "name",
        sortKey: "last_name",
        dataMapFunction: ({ id, name }) => ({
          text: name,
          href: PageRoutes.USER_DETAIL.replace(":userId", `${id}`),
        }),
        dataMapFunctionForPopup: ({ name }) => name,
        customComponent: Link,
      },
      {
        id: 2,
        title: "所属",
        popupTitle: "所属組織名",
        dataKey: "clientName",
        sortKey: "customers.name",
      },
      {
        id: 3,
        title: "グループ",
        popupTitle: "所属グループ",
        dataKey: "groupName",
        sortKey: "groups.name",
      },
      { id: 4, title: "権限", dataKey: "role", sortKey: "role" },
      {
        id: 5,
        title: "有効期間",
        dataKey: "validPeriod",
        dataMapFunction: ({ validPeriod }) =>
          `${formatDateTime(validPeriod.from, false, "YYYY/MM/DD HH:mm")}〜${formatDateTime(validPeriod.to, false, "YYYY/MM/DD HH:mm")}`,
        sortKey: "valid_from",
      },
      {
        id: 6,
        title: "システム許可",
        popupTitle: "アクセス権",
        dataKey: "systemPermissions",
        dataMapFunction: ({ systemPermissions }) =>
          systemPermissions.join("／"),
      },
      {
        id: 7,
        title: "最終ログイン",
        dataKey: "lastLogin",
        dataMapFunction: ({ lastLogin }) =>
          !lastLogin
            ? "未ログイン"
            : formatDateTime(lastLogin, false, "YYYY/MM/DD HH:mm"),
        sortKey: "last_login",
      },
    ];
  }, []);

  const handleRegistration = useCallback(() => {
    navigateToPage(PageRoutes.USER_REGISTRATION);
  }, []);

  const handleEdit = useCallback((user: User) => {
    navigateToPage(PageRoutes.USER_EDIT.replace(":userId", `${user.id}`));
  }, []);

  return {
    canCreate,
    users,
    tableHeaders,
    fetchUsers,
    handleRegistration,
    handleEdit,
  };
};
